import React from "react";

/* ═══════════════════════════════════════════════════════════════════════
   PARIKSA LOGO
   Gauge logomark + optional wordmark / devanagari / tagline
   Adapts to light and dark surfaces via parent [data-theme]
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Props:
 *   size     – pixel size of the gauge (default 56). Aspect ratio 240:260.
 *   variant  – "icon" | "full" (full = gauge + wordmark stack)
 *   showAI   – boolean, renders "AI" text under gauge (default: size >= 96)
 *   showGrid – boolean, renders inner dashed ring (default: size >= 80)
 *   showTicks – boolean, renders outer tick marks (default: size >= 90)
 *   className – extra classes
 */
export default function PariksaLogo({
  size = 56,
  variant = "icon",
  showAI,
  showGrid,
  showTicks,
  className = "",
}) {
  const h = Math.round(size * (260 / 240));
  const _showAI = showAI ?? size >= 96;
  const _showGrid = showGrid ?? size >= 80;
  const _showTicks = showTicks ?? size >= 90;

  const gauge = (
    <svg
      width={size}
      height={h}
      viewBox="0 0 240 260"
      xmlns="http://www.w3.org/2000/svg"
      className={`pariksa-gauge ${className}`}
      role="img"
      aria-label="Assayo gauge"
    >
      <g transform="translate(120, 120)">
        {/* outer faint ring */}
        <circle cx="0" cy="0" r="105" fill="none" stroke="#1E4E8C" strokeWidth="1" opacity="0.2" />
        {/* main steel arc */}
        <path d="M -85,35 A 92,92 0 1,1 85,35" fill="none" stroke="#1E4E8C" strokeWidth="14" strokeLinecap="round" />
        {/* inner track (theme-aware via CSS) */}
        <path d="M -65,28 A 70,70 0 1,1 65,28" fill="none" className="pariksa-inner-track" strokeWidth="5" strokeLinecap="round" />
        {/* mid-blue progress arc */}
        <path d="M -65,28 A 70,70 0 1,0 55,-42" fill="none" stroke="#5790C9" strokeWidth="5" strokeLinecap="round" />

        {/* dashed inner grid ring */}
        {_showGrid && (
          <circle cx="0" cy="0" r="48" fill="none" stroke="#1E4E8C" strokeWidth="0.8" opacity="0.25" strokeDasharray="3,3" />
        )}

        {/* outer tick marks */}
        {_showTicks && (
          <g className="pariksa-ticks" strokeWidth="1.8" strokeLinecap="round" opacity="0.4">
            <line x1="-88" y1="26"  x2="-78" y2="22" />
            <line x1="-92" y1="-12" x2="-82" y2="-10" />
            <line x1="-78" y1="-48" x2="-68" y2="-43" />
            <line x1="-48" y1="-78" x2="-43" y2="-68" />
            <line x1="0"   y1="-92" x2="0"   y2="-82" />
            <line x1="48"  y1="-78" x2="43"  y2="-68" />
            <line x1="78"  y1="-48" x2="68"  y2="-43" />
            <line x1="92"  y1="-12" x2="82"  y2="-10" />
            <line x1="88"  y1="26"  x2="78"  y2="22" />
          </g>
        )}

        {/* constellation nodes (only at larger sizes) */}
        {_showGrid && (
          <g opacity="0.9">
            <circle cx="-60" cy="-65" r="2.5" fill="#2E70AD" opacity="0.5" />
            <circle cx="25"  cy="-82" r="2.5" fill="#2E70AD" opacity="0.5" />
            <circle cx="72"  cy="-30" r="2.5" fill="#2E70AD" opacity="0.5" />
            <circle cx="-75" cy="5"   r="2.5" fill="#2E70AD" opacity="0.5" />
            <line x1="-58" y1="-63" x2="-5" y2="-5" stroke="#2E70AD" strokeWidth="0.5" opacity="0.15" />
            <line x1="24"  y1="-80" x2="2"  y2="-5" stroke="#2E70AD" strokeWidth="0.5" opacity="0.15" />
            <line x1="70"  y1="-28" x2="5"  y2="-2" stroke="#2E70AD" strokeWidth="0.5" opacity="0.15" />
            <line x1="-73" y1="5"   x2="-5" y2="0"  stroke="#2E70AD" strokeWidth="0.5" opacity="0.15" />
          </g>
        )}

        {/* ember needle */}
        <line x1="0" y1="0" x2="62" y2="-52" stroke="#F26B1F" strokeWidth="3" strokeLinecap="round" />
        <circle cx="62" cy="-52" r="8" fill="#F26B1F" opacity="0.12" />
        <circle cx="62" cy="-52" r="4" fill="#F26B1F" />

        {/* hub */}
        <circle cx="0" cy="0" r="10" fill="#143A6B" />
        <circle cx="0" cy="0" r="5"  fill="#F26B1F" />

        {/* AI badge */}
        {_showAI && (
          <text x="0" y="155" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif"
                fontSize="22" fill="#F26B1F" fontWeight="600" letterSpacing="5">AI</text>
        )}
      </g>
    </svg>
  );

  if (variant === "icon") return gauge;

  // Full lockup (gauge + wordmark stack)
  return (
    <div className={`pariksa-lockup ${className}`}>
      {gauge}
      <div className="pariksa-wordmark-stack">
        <span className="pariksa-wordmark" style={{ fontSize: Math.round(size * 0.48) }}>
          <span className="pariksa-base">Assa</span>
          <span className="pariksa-k">y</span>
          <span className="pariksa-base">o</span>
        </span>
        <span className="pariksa-tagline" style={{ fontSize: Math.round(size * 0.086) }}>
          <span className="pariksa-ai">AI</span>
          <span className="pariksa-pwd">&nbsp;POWERED&nbsp;</span>
          <span className="pariksa-tag">MATURITY ASSESSMENT</span>
        </span>
      </div>
    </div>
  );
}

/* Named export for the simple wordmark (text-only, inline) */
export function PariksaWordmark({ size = 18 }) {
  return (
    <span className="pariksa-wordmark-inline" style={{ fontSize: size }}>
      <span className="pariksa-base">Assa</span>
      <span className="pariksa-k">y</span>
      <span className="pariksa-base">o</span>
    </span>
  );
}
